import React, { useState, useEffect, useRef } from "react";
import "./MarketView.css";
import TradingChart from "./TradingChart";

function MarketView() {
  const [marketData, setMarketData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Hot");
  const [isConnected, setIsConnected] = useState(true);
  const [selectedCoinForModal, setSelectedCoinForModal] = useState(null);
  const wsRef = useRef(null);

  // Top 20 coins with image URLs
  const topCoins = [
    "BTCUSDT", "ETHUSDT", "BNBUSDT", "SOLUSDT", "XRPUSDT",
    "ADAUSDT", "AVAXUSDT", "DOTUSDT", "DOGEUSDT", "BCHUSDT",
    "LTCUSDT", "LINKUSDT", "MATICUSDT", "XLMUSDT", "EOSUSDT",
    "XTZUSDT", "XMRUSDT", "ETCUSDT", "ALGOUSDT", "ZECUSDT"
  ];

  // Coin information with images
  const coinInfo = {
    BTCUSDT: { 
      symbol: "BTC",
      color: "#F7931A",
      image: "https://www.coinwor.com/symbol/btc.png"
    },
    ETHUSDT: { 
      symbol: "ETH",
      color: "#627EEA",
      image: "https://www.coinwor.com/symbol/eth.png"
    },
    BNBUSDT: { 
      symbol: "BNB",
      color: "#F3BA2F",
      image: "https://www.coinwor.com/symbol/bnb.png"
    },
    SOLUSDT: { 
      symbol: "SOL",
      color: "#00FFA3",
      image: "https://www.coinwor.com/symbol/sol.png"
    },
    XRPUSDT: { 
      symbol: "XRP",
      color: "#25A768",
      image: "https://www.coinwor.com/symbol/xrp.png"
    },
    ADAUSDT: { 
      symbol: "ADA",
      color: "#0033AD",
      image: "https://www.coinwor.com/symbol/ada.png"
    },
    AVAXUSDT: { 
      symbol: "AVAX",
      color: "#E84142",
      image: "https://www.coinwor.com/symbol/avax.png"
    },
    DOTUSDT: { 
      symbol: "DOT",
      color: "#E6007A",
      image: "https://www.coinwor.com/symbol/dot.png"
    },
    DOGEUSDT: { 
      symbol: "DOGE",
      color: "#C2A633",
      image: "https://www.coinwor.com/symbol/doge.png"
    },
    BCHUSDT: { 
      symbol: "BCH",
      color: "#0AC18E",
      image: "https://www.coinwor.com/symbol/bch.png"
    },
    LTCUSDT: {  
      symbol: "LTC",
      color: "#BFBBBB",
      image: "https://www.coinwor.com/symbol/ltc.png"
    },
    LINKUSDT: { 
      symbol: "LINK",
      color: "#2A5ADA",
      image: "https://www.coinwor.com/symbol/link.png"
    },
    MATICUSDT: { 
      symbol: "MATIC",
      color: "#8247E5",
      image: "https://www.coinwor.com/symbol/matic.png"
    },
    XLMUSDT: { 
      symbol: "XLM",
      color: "#000000",
      image: "https://www.coinwor.com/symbol/xlm.png"
    },
    EOSUSDT: { 
      symbol: "EOS",
      color: "#000000",
      image: "https://www.coinwor.com/symbol/eos.png"
    },
    XTZUSDT: { 
      symbol: "XTZ",
      color: "#2C7DF7",
      image: "https://www.coinwor.com/symbol/xtz.png"
    },
    XMRUSDT: { 
      symbol: "XMR",
      color: "#FF6600",
      image: "https://www.coinwor.com/symbol/xmr.png"
    },
    ETCUSDT: { 
      symbol: "ETC",
      color: "#3AB83A",
      image: "https://www.coinwor.com/symbol/etc.png"
    },
    ALGOUSDT: { 
      symbol: "ALGO",
      color: "#000000",
      image: "https://www.coinwor.com/symbol/algo.png"
    },
    ZECUSDT: { 
      symbol: "ZEC",
      color: "#F4B728",
      image: "https://www.coinwor.com/symbol/zec.png"
    },
  };

  // Step 1 — Fetch initial data via REST API
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://api.binance.com/api/v3/ticker/24hr");
        const data = await response.json();

        // Filter top 20
        const filtered = data.filter((coin) => topCoins.includes(coin.symbol));
        const formatted = {};
        filtered.forEach((coin) => {
          formatted[coin.symbol] = {
            current_price: parseFloat(coin.lastPrice),
            price_change_percentage_24h: parseFloat(coin.priceChangePercent),
            volume_24h: parseFloat(coin.volume),
            price_change_24h: parseFloat(coin.priceChange),
          };
        });
        setMarketData(formatted);
      } catch (err) {
        console.error("Error fetching Binance data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Step 2 — WebSocket live updates
  useEffect(() => {
    if (!topCoins.length) return;

    // Create combined stream
    const streams = topCoins.map((s) => `${s.toLowerCase()}@ticker`).join("/");
    const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      console.log("WebSocket connected");
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      const update = message.data;
      const symbol = update.s;
      setMarketData((prev) => ({
        ...prev,
        [symbol]: {
          current_price: parseFloat(update.c),
          price_change_percentage_24h: parseFloat(update.P),
          volume_24h: parseFloat(update.v),
          price_change_24h: parseFloat(update.p),
        },
      }));
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      setIsConnected(false);
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      setIsConnected(false);
    };

    return () => ws.close();
  }, []);

  // Filter + sort logic
  const getFilteredPairs = () => {
    let pairs = Object.keys(marketData)
      .filter((s) => {
        const coin = coinInfo[s];
        return coin && (
          s.includes(searchTerm.toUpperCase()) ||
          coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          coin.symbol.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .map((symbol) => ({
        symbol,
        ...coinInfo[symbol],
        ...marketData[symbol],
      }));

    if (activeTab === "Gainers") {
      pairs = pairs.filter((p) => p.price_change_percentage_24h > 0);
    } else if (activeTab === "Losers") {
      pairs = pairs.filter((p) => p.price_change_percentage_24h < 0);
    }

    return pairs.sort((a, b) => {
      if (activeTab === "Hot") return b.volume_24h - a.volume_24h;
      if (activeTab === "Gainers") return b.price_change_percentage_24h - a.price_change_percentage_24h;
      if (activeTab === "Losers") return a.price_change_percentage_24h - b.price_change_percentage_24h;
      return 0;
    });
  };

  const pairs = getFilteredPairs();

  const formatPrice = (price) => {
    if (!price) return "0.00";
    if (price < 1) return price.toFixed(6);
    if (price < 10) return price.toFixed(4);
    if (price < 1000) return price.toFixed(2);
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatVolume = (volume) => {
    if (!volume) return "0.00";
    if (volume < 1000) return volume.toFixed(2);
    if (volume < 1000000) return (volume / 1000).toFixed(2) + "K";
    if (volume < 1000000000) return (volume / 1000000).toFixed(2) + "M";
    return (volume / 1000000000).toFixed(2) + "B";
  };

  const formatMarketCap = (price, volume) => {
    // Simplified market cap calculation
    const marketCap = price * (volume / price) * 100; // Approximation
    if (marketCap < 1000000) return (marketCap / 1000).toFixed(2) + "K";
    if (marketCap < 1000000000) return (marketCap / 1000000).toFixed(2) + "M";
    if (marketCap < 1000000000000) return (marketCap / 1000000000).toFixed(2) + "B";
    return (marketCap / 1000000000000).toFixed(2) + "T";
  };

  const openModal = (coin) => {
    setSelectedCoinForModal(coin);
  };

  const closeModal = () => {
    setSelectedCoinForModal(null);
  };

  if (loading) {
    return (
      <div className="market-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading  market data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="market-view">
      {/* Header Section */}
      <div className="market-header">
        <div className="header-top">
          <div className="title-section">
            <div className="title-with-badge">
              <h1>Cryptocurrency Markets</h1>
            </div>
            <p className="subtitle">Real-time cryptocurrency prices and market data</p>
          </div>
          
          <div className="header-controls">
            
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-section">
          <div className="tabs-container">
            <div className="tabs">
              {["All", "Hot", "Gainers", "Losers"].map((tab) => (
                <button
                  key={tab}
                  className={`tab-button ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                  {tab === "Gainers" && <span className="tab-indicator up">↗</span>}
                  {tab === "Losers" && <span className="tab-indicator down">↘</span>}
                </button>
              ))}
            </div>
            <div className="market-stats">
            </div>
          </div>
        </div>
      </div>

      {/* Market Table */}
      <div className="market-table-container">
        <div className="market-table">
          {/* Table Header */}
          <div className="table-header">
            <div className="table-col coin-col">Asset</div>
            <div className="table-col price-col">Price</div>
            <div className="table-col change-col">24h Change</div>
            <div className="table-col volume-col">24h Volume</div>
            <div className="table-col market-cap-col">Market Cap</div>
          </div>

          {/* Table Body */}
          <div className="table-body">
            {pairs.length > 0 ? (
              pairs.map((p) => (
                <div 
                  key={p.symbol}
                  className="table-row"
                  onClick={() => openModal(p)}
                >
                  <div className="table-col coin-col">
                    <div className="coin-info">
                      <div 
                        className="coin-icon"
                        style={{ backgroundColor: p.color + '20' }}
                      >
                        <img src={p.image} alt={p.name} />
                      </div>
                      <div className="coin-details">
                        <div className="coin-name-symbol">
                          <span className="coin-name">{p.name}</span>
                          <span className="coin-symbol">{p.symbol}</span>
                        </div>
                        <div className="coin-full-name">{p.name}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-col price-col">
                    <div className="price-value">${formatPrice(p.current_price)}</div>
                    <div className="price-change-absolute">
                      {p.price_change_24h >= 0 ? '+' : ''}{formatPrice(p.price_change_24h)}
                    </div>
                  </div>
                  
                  <div className="table-col change-col">
                    <div className={`change-indicator ${p.price_change_percentage_24h >= 0 ? "positive" : "negative"}`}>
                      <span className="change-value">{p.price_change_percentage_24h >= 0 ? '+' : ''}{p.price_change_percentage_24h.toFixed(2)}%</span>
                      {p.price_change_percentage_24h >= 0 ? (
                        <svg className="change-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="change-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-col volume-col">
                    <div className="volume-value">${formatVolume(p.volume_24h)}</div>
                  </div>
                  
                  <div className="table-col market-cap-col">
                    <div className="market-cap-value">
                      ${formatMarketCap(p.current_price, p.volume_24h)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <div className="no-results-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3>No cryptocurrencies found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="market-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <span className="footer-label">Last Updated:</span>
            <span className="footer-value">{new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Chart Modal */}
      {selectedCoinForModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="modal-header">
              <div className="coin-info-modal">
                <div 
                  className="coin-icon"
                  style={{ backgroundColor: selectedCoinForModal.color + '20' }}
                >
                  <img src={selectedCoinForModal.image} alt={selectedCoinForModal.name} />
                </div>
                <h2>{selectedCoinForModal.name} ({selectedCoinForModal.symbol})</h2>
              </div>
            </div>
            <div className="modal-chart-container">
              <TradingChart 
                pair={selectedCoinForModal.symbol} 
                marketData={{ [selectedCoinForModal.symbol]: selectedCoinForModal }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketView;